import { useEffect, useState } from "react";

// 1. Define the TypeScript interface for the data we expect back
interface GeolocationData {
  ip: string;
  city: string;
  latitude: string;
  longitude: string;
  // You can expand this to include more fields like region, country, etc.
  [key: string]: any;
}

// 2. Define the hook's return type (tuple for clean array destructuring)
type UseGeolocationResult = [GeolocationData | null, boolean, string | null];

/**
 * Custom hook to fetch the user's IP address and city location.
 * It uses the ipinfo.io service, which automatically determines the user's
 * IP from the request origin and returns the corresponding location data.
 *
 * @returns {[GeolocationData | null, boolean, string | null]} - An array containing:
 * [0] The geolocation data object (or null),
 * [1] A boolean indicating if the data is currently loading,
 * [2] An error message string (or null).
 */
export const useGeolocation = (): UseGeolocationResult => {
  const [data, setData] = useState<GeolocationData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchLocation = async () => {
      try {
        const response = await fetch("https://ipinfo.io/json");

        if (!response.ok) {
          // Throw an error if the API call fails (e.g., 404, 500)
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result: GeolocationData = await response.json();

        // Ensure the expected fields are present (good practice for external APIs)
        if (result.ip && result.city && result.loc) {
          const [latitude, longitude] = result.loc.split(",");
          setData({ ...result, latitude, longitude });
        } else {
          throw new Error("Geolocation data is incomplete or invalid.");
        }
      } catch (e) {
        // TypeScript standard: check if the error is a standard Error object
        if (e instanceof Error) {
          setError(e.message);
        } else {
          setError("An unknown error occurred during location fetch.");
        }
      } finally {
        // This runs regardless of success or failure
        setIsLoading(false);
      }
    };

    fetchLocation();
  }, []); // The empty dependency array ensures this effect runs only once on mount

  return [data, isLoading, error];
};
